import { packEMS } from './ems';
import { Item as V5Item, Bin as V5Bin, Packer as V5Packer } from '../../bin-packing-v5-current/3D';

const FACTOR = 1e5; // factoredInteger 스케일

/**
 * 3D 빈패킹 오케스트레이터 (v6, 하이브리드).
 *
 * 파레트마다 두 알고리즘을 모두 실행하고 "더 많이 담는 쪽"을 채택한다:
 *   - EMS 코어(ems.js): 혼합/오버플로 적재에서 v5 대비 최대 +20% 더 담음
 *   - v5 폴백(bin-packing-v5-current): 균일(단일 상품) 격자 적재에서 강함
 * max(v5, EMS) 이므로 v5보다 나빠질 수 없고(무회귀), 이길 수 있을 때만 이긴다.
 *
 * 공개 계약(사용처 보존): addBin / addItem / pack() / bin.items[].name /
 * unfitItems[].name / item.position / item.getDimension().
 */
export default class Packer {

  constructor() {
    this.bins = [];
    this.items = [];
    this.unfitItems = [];
  }

  addBin(bin) {
    this.bins.push(bin);
  }

  addItem(item) {
    this.items.push(item);
  }

  pack() {
    const bin = this.bins[0];
    if (!bin) {
      return null;
    }

    // --- 시도 A: EMS ---
    const ems = packEMS(bin.width, bin.height, bin.depth, this.items);

    // --- 시도 B: v5 폴백 (동일 상자를 v5 객체로 재구성해 실행) ---
    const v5 = this.runV5Fallback(bin);

    // 더 많이 담은 쪽 채택 (동수면 v5 우선 — 검증된 격자 배치 유지)
    const useEMS = ems.count > v5.placedNames.size;
    const placedNames = useEMS ? ems.placedNames : v5.placedNames;
    const positions = useEMS ? ems.positions : v5.positions;

    // 결과를 실제 bin.items / unfitItems 에 반영
    bin.items = [];
    this.unfitItems = [];
    for (const item of this.items) {
      const placement = placedNames.has(item.name) ? positions.get(item.name) : null;
      if (placement) {
        item.position = placement.position;
        item.rotationType = placement.rotationType;
        bin.items.push(item);
      } else {
        this.unfitItems.push(item);
      }
    }

    return null;
  }

  /**
   * v5 알고리즘을 폴백으로 실행한다. v6 아이템/빈을 v5 객체로 재구성한다.
   * (factoredInteger는 정수 mm에 대해 멱등이므로 factored 값을 1e5로 나눠 되돌려도 안전)
   */
  runV5Fallback(bin) {
    const packer = new V5Packer();
    const v5bin = new V5Bin(bin.name, bin.width / FACTOR, bin.height / FACTOR, bin.depth / FACTOR, bin.maxWeight / FACTOR);
    packer.addBin(v5bin);

    for (const item of this.items) {
      // allowedRotation은 이미 mustBeFlat 필터가 적용된 상태 → v5에는 flat=false로 그대로 전달
      packer.addItem(new V5Item(
        item.name,
        item.width / FACTOR,
        item.height / FACTOR,
        item.depth / FACTOR,
        item.weight / FACTOR,
        item.allowedRotation.slice(),
        false
      ));
    }

    packer.pack();

    const placedNames = new Set();
    const positions = new Map();
    for (const placed of v5bin.items) {
      placedNames.add(placed.name);
      positions.set(placed.name, {
        position: placed.position.slice(),
        rotationType: placed.rotationType,
      });
    }
    return { placedNames, positions };
  }
}
